import { Alert, Box } from '@mui/material';
import { isNil, some } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
    SoundPressureBandStatsSample,
    SoundPressureQuietSample,
    useCurrentsData,
    useDissolvedOxygenData,
    usePressureData,
    useSalinityData,
    useSoundPressureData,
    useTemperatureData,
    useTurbidityData,
} from '@/api/historicalData';
import { sensorKey } from '@/api/historicalData/helpers';
import { Line } from '@/charts';
import { flattenSensorPositions, linesAcrossSensors } from '@/charts/common';
import { GenericObject } from '@/charts/helpers';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { CURRENT_LINE } from '@/components/spotter/historicalDataTab/CurrentsLineChartSection';
import { LINE_CONCENTRATION } from '@/components/spotter/historicalDataTab/DissolvedOxygenSection';
import { SALINITY_LINE_CONFIG_BASE } from '@/components/spotter/historicalDataTab/SalinityLineChartSection';
import { SensorLineChart, SensorQueryData } from '@/components/spotter/historicalDataTab/SensorLineChartSection';
import { BROADBAND_SPL_LINE } from '@/components/spotter/historicalDataTab/SoundPressureSection';
import { SUBSURFACE_TEMPERATURE_LINE } from '@/components/spotter/historicalDataTab/SubsurfaceTemperatureSection';
import { TURBIDITY_LINE } from '@/components/spotter/historicalDataTab/TurbiditySection';
import { WATER_PRESSURE_LINE } from '@/components/spotter/historicalDataTab/WaterPressureSection';
import { AlertTriggerReferenceChartProps } from '@/components/spotter/notificationTab/AlertReferenceChart';
import { useAlertsFromForm } from '@/components/spotter/notificationTab/useAlertsFromForm';
import { useDateRangeContext } from '@/context/DateRangeContext';

export const BroadbandSplReferenceChart = ({ enabled = false }: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = useSoundPressureData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    // bandStats makes it not a GenericObject, but we don't actually care
    const sensorDataWithoutBandStats = sensorData as SensorQueryData<
        Omit<SoundPressureBandStatsSample | SoundPressureQuietSample, 'bandStats'>
    >;

    return (
        <SensorAlertReferenceChart
            chartId="broadband-spl-alert"
            chartTitle={'Sound pressure alert'}
            yAxisLabel="Broadband SPL"
            sensorData={sensorDataWithoutBandStats}
            baseLine={BROADBAND_SPL_LINE}
        />
    );
};

export const CurrentsAlertReferenceChart = ({ enabled = false }: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = useCurrentsData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    return (
        <SensorAlertReferenceChart
            chartId="currents-speed-alert"
            chartTitle={'Current speed alert'}
            yAxisLabel="Current speed"
            sensorData={sensorData}
            baseLine={CURRENT_LINE}
        />
    );
};

export const DisslovedOxygenConcentrationAlertReferenceChart = ({
    enabled = false,
}: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = useDissolvedOxygenData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    return (
        <SensorAlertReferenceChart
            chartId="dissolved-oxygen-concentration-alert"
            chartTitle={'Dissolved oxygen alert'}
            yAxisLabel="Oxygen concentration"
            sensorData={sensorData}
            baseLine={LINE_CONCENTRATION}
        />
    );
};

export const SubsurfaceTemperatureAlertReferenceChart = ({ enabled = false }: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = useTemperatureData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    return (
        <SensorAlertReferenceChart
            chartId="subsurface-temperature-alert"
            chartTitle={'Subsurface temperature alert'}
            yAxisLabel="Water temperature"
            sensorData={sensorData}
            baseLine={SUBSURFACE_TEMPERATURE_LINE}
        />
    );
};

export const SalinityAlertReferenceChart = ({ enabled = false }: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = useSalinityData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    return (
        <SensorAlertReferenceChart
            chartId="salinity-alert"
            chartTitle={'Salinity alert'}
            yAxisLabel="Salinity"
            sensorData={sensorData}
            baseLine={SALINITY_LINE_CONFIG_BASE}
        />
    );
};

export const TurbidityAlertReferenceChart = ({ enabled = false }: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = useTurbidityData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    return (
        <SensorAlertReferenceChart
            chartId="turbidity-alert"
            chartTitle={'Turbidity alert'}
            yAxisLabel="Turbidity"
            sensorData={sensorData}
            baseLine={TURBIDITY_LINE}
        />
    );
};

export const WaterPressureAlertReferenceChart = ({ enabled = false }: AlertTriggerReferenceChartProps) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const { dateRange } = useDateRangeContext();

    const sensorData = usePressureData({
        spotterId,
        dateRange,
        config: { enabled },
    });

    return (
        <SensorAlertReferenceChart
            chartId="water-pressure-alert"
            chartTitle={'Water pressure alert'}
            yAxisLabel="Pressure"
            sensorData={sensorData}
            baseLine={WATER_PRESSURE_LINE}
        />
    );
};

export type SensorAlertReferenceChartProps<Datum extends GenericObject> = {
    chartId: string;
    chartTitle: string;
    yAxisLabel: string;
    sensorData: SensorQueryData<Datum>;
    baseLine: Omit<Line<Datum>, 'color' | 'dataFilter'>;
};

export const SensorAlertReferenceChart = <Datum extends GenericObject>({
    chartTitle,
    chartId,
    yAxisLabel,
    sensorData: { data: sensorData, isFetching },
    baseLine,
}: SensorAlertReferenceChartProps<Datum>) => {
    const { spotterId } = useParams<{ spotterId: string }>();
    const alerts = useAlertsFromForm();
    const filterSensorPositions = useMemo(() => {
        if (some(alerts, ({ sensorPosition }) => isNil(sensorPosition))) {
            // no filter
            return undefined;
        }
        return alerts?.map(({ sensorPosition }) => sensorKey(sensorPosition!)) ?? [];
    }, [alerts]);

    const { sensorPositions, dataset } = useMemo(
        () => flattenSensorPositions(sensorData, { filterSensorPositions }),
        [sensorData, filterSensorPositions]
    );

    const lines = useMemo(
        () => linesAcrossSensors([baseLine] as unknown as Line<Datum & { sensorPosition: string }>[], sensorPositions),
        [sensorPositions, baseLine]
    );

    if (!spotterId) {
        return;
    }

    if (!isFetching && !dataset.length) {
        return (
            <Alert severity="info">
                No {yAxisLabel.toLowerCase()} data for selected date range
                {filterSensorPositions?.length
                    ? ` at sensor position ${filterSensorPositions.map((p) => p.replace(/sensor-/, ''))}`
                    : ''}
            </Alert>
        );
    }

    return (
        <Box sx={{ position: 'relative', width: 1, height: 1 }}>
            {isFetching && (
                <LoadingIndicatorIcon
                    isLoading={isFetching}
                    loadingSx={{ position: 'absolute', bottom: '50%', left: '50%' }}
                />
            )}
            <SensorLineChart
                chartId={chartId}
                chartTitle={chartTitle}
                spotterId={spotterId}
                showAlertLines={true}
                scaleToFitAlerts={true}
                yAxisLabel={yAxisLabel}
                dataset={dataset}
                lines={lines}
                alertLines={alerts}
            />
        </Box>
    );
};
